import { Chip } from '@mui/material';
import {
  type CSSProperties,
  memo,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { components, type GroupBase, type MenuListProps } from 'react-select';
import {
  AsyncPaginate,
  type LoadOptions,
  type ShouldLoadMore,
  wrapMenuList,
} from 'react-select-async-paginate';

import { promiseAllThrottled } from '@vestigas/utils';

import { Spinner } from '../Spinner';
import { ValidationError } from '../TanstackForm';

// import { EMPTY_DROPDOWN_OPTION } from '~/constants/select';
export const EMPTY_DROPDOWN_OPTION = '(leer)'; // TODO: get from some global constants

export type Option<T = any> = {
  value: string;
  label: string;
  data: T;
};

/**
 * What callers can do with the mounted select instance via `selectRef` — just
 * enough to focus it imperatively, without depending on react-select's full
 * (and here, mismatched) `SelectInstance` type.
 */
export type SelectFocusHandle = {
  readonly focus: () => void;
};

export type SelectServerDrivenProps<T> = {
  /**
   * Accessible name for the select, for the cases where a visible `InputLabel`
   * would be redundant — e.g. one select per row of a table whose first column
   * already names the row.
   */
  readonly ariaLabel?: string;
  readonly debounceTime?: number;
  readonly errors?: string;
  readonly filterActive?: boolean;
  readonly formatOptionLabel?: (
    option: Option<T>,
    formatOptionLabelMeta: any,
  ) => ReactNode;
  readonly getItemData: (value: string) => Promise<T | undefined>;
  readonly getOptionLabel: (item: T) => string;
  readonly id?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isItemSelectionAllowed: () => boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<T>) => boolean;
  readonly loadOptions: LoadOptions<
    Option<T>,
    GroupBase<Option<T>>,
    { page: number; filterActive?: boolean | undefined }
  >;
  readonly onChange: {
    (value: string[], data: T[]): void;
    (value: string, data: T): void;
  };
  readonly onMultiValueClick?: (data: Option<T>) => void;
  readonly placeholder?: string;
  /**
   * Registers the mounted select instance, e.g. so a caller can focus it with
   * `requestAnimationFrame` right after mounting it (see `AutocompleteField`'s
   * `ref` for the same pattern on a plain input).
   */
  readonly selectRef?: (instance: SelectFocusHandle | null) => void;
  readonly value: string | string[];
} & ComponentStyling;

const hideComponent = () => null;

// Always return the value as an array.
const ensureArray = (value: string | string[]) => [value].flat();

/**
 * A function used to determine if the select should try to load more options if available.
 * Made it so it will return true if the user is 2/3 of the way down
 * This looks like a good compromise for loading additional options without adding too much delay.
 *
 * @param scrollHeight - The height of the scrollable container
 * @param clientHeight - The height of the visible area of the scrollable container
 * @param scrollTop - The distance of the scrollable container from the top of the page
 * @returns Whether the load more button should be shown
 */
const shouldLoadMore: ShouldLoadMore = (
  scrollHeight,
  clientHeight,
  scrollTop,
) => {
  const bottomBorder = ((scrollHeight - clientHeight) * 2) / 3;

  return bottomBorder < scrollTop;
};

const CustomMenuList = memo(
  (
    props: MenuListProps & {
      readonly isLoading: boolean;
      readonly options: any[];
    },
  ) => {
    const { isLoading, options, children } = props;

    return (
      <components.MenuList {...props}>
        {children}
        {isLoading && options.length > 0 ? <Spinner className="py-4" /> : null}
      </components.MenuList>
    );
  },
);

/**
 * SelectServerDriven is a flexible, server-driven select component supporting both single and multi-select modes.
 * It leverages react-select-async-paginate for efficient server-side pagination.
 *
 * @param ariaLabel - Accessible name, for selects rendered without a visible label.
 * @param className - Additional CSS class for styling the select component.
 * @param classNames - Additional CSS classes for styling components of the select component.
 * @param debounceTime - Debounce duration for search input in milliseconds (default: 300).
 * @param errors - Error messages related to the select component.
 * @param formatOptionLabel - Function to format the display label of options.
 * @param getItemData - Function to retrieve item data for a specified value.
 * @param getOptionLabel - Function to extract the label string from an item.
 * @param id - ID forwarded to the select input for label association.
 * @param isClearable - Indicates if the selection can be cleared (default: true).
 * @param isDisabled - Indicates if the select component is disabled (default: false).
 * @param isItemSelectionAllowed - Function to check if item selection is permitted.
 * @param isMultiSelect - Indicates if multiple selections are allowed (default: false).
 * @param isRemovableValue - Function to determine if a selected value can be removed by the user.
 * @param loadOptions - Function to fetch options from the server.
 * @param onChange - Callback triggered when the selection changes.
 * @param onMultiValueClick - Callback triggered when a multi-value item is clicked.
 * @param placeholder - Placeholder text for the select input (default: 'Element auswählen').
 * @param style - Inline styles for the select component.
 * @param value - Currently selected value(s).
 *
 * @returns JSX.Element - The rendered select component.
 *
 * @see https://github.com/vtaits/react-select-async-paginate/tree/master/packages/react-select-async-paginate
 * @see https://react-select.com/props
 */
export const SelectServerDriven = <T,>({
  ariaLabel,
  className,
  classNames,
  debounceTime = 300,
  errors,
  formatOptionLabel,
  getItemData,
  getOptionLabel,
  id,
  isItemSelectionAllowed = () => true,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = false,
  isRemovableValue,
  loadOptions,
  onChange,
  onMultiValueClick,
  placeholder = 'Element auswählen',
  selectRef,
  style,
  value,
  filterActive = true,
}: SelectServerDrivenProps<T>): JSX.Element => {
  const { t } = useTranslation('ui');

  const [selectedItems, setSelectedItems] = useState<Array<Option<T>>>([]);

  const [error, setError] = useState<string | undefined>(undefined);

  const getSelectedItems = useCallback(
    async (value: any, getItemData: any, getOptionLabel: any) => {
      try {
        // If value is empty string or empty array, clear selection
        if (
          !value ||
          (Array.isArray(value) && value.filter(Boolean).length === 0)
        ) {
          setSelectedItems([]);
          return;
        }

        const items = await promiseAllThrottled(
          ensureArray(value)
            .filter(Boolean) // Only filter out falsy values
            .map(async (value_) => {
              // Special handling for 'empty' option
              if (value_ === EMPTY_DROPDOWN_OPTION) {
                return {
                  data: {
                    id: EMPTY_DROPDOWN_OPTION,
                    name: EMPTY_DROPDOWN_OPTION,
                  },
                  label: EMPTY_DROPDOWN_OPTION,
                  value: EMPTY_DROPDOWN_OPTION,
                };
              }

              const item = await getItemData(value_);

              return item
                ? {
                    data: item,
                    label: getOptionLabel(item),
                    value: value_,
                  }
                : null;
            }),
        );

        setSelectedItems(
          items.filter(
            (item): item is Option<T> => item !== null && item !== undefined,
          ),
        );
      } catch (error) {
        setError('Failed to fetch selected items. Please try again.');
        console.error('Error fetching selected items:', error);
      }
    },
    [value, getItemData, getOptionLabel],
  );

  useLayoutEffect(() => {
    void getSelectedItems(value, getItemData, getOptionLabel);
  }, [value, getItemData, getOptionLabel, getSelectedItems]);

  const preserveNonRemovableValues = useCallback(
    (nextSelectedItems: Array<Option<T>>) => {
      if (!isMultiSelect || typeof isRemovableValue !== 'function') {
        return nextSelectedItems;
      }

      // Walk the original order: keep locked items and items still selected
      const merged = selectedItems.filter(
        (item) =>
          !isRemovableValue(item) ||
          nextSelectedItems.some(({ value }) => value === item.value),
      );

      // Append newly added items (not previously in selectedItems)
      const brandNew = nextSelectedItems.filter(
        (item) => !selectedItems.some(({ value }) => value === item.value),
      );

      return [...merged, ...brandNew];
    },
    [isMultiSelect, isRemovableValue, selectedItems],
  );

  const handleChange = useCallback(
    (newValue: Option<T> | Array<Option<T>> | null | undefined) => {
      const next = [newValue]
        .flat()
        .filter((item): item is Option<T> => item != null);
      const newSelectedItems = preserveNonRemovableValues(next);

      setSelectedItems(newSelectedItems);

      const newValues = newSelectedItems.map(({ value }) => value);
      const newData = newSelectedItems.map(({ data }) => data);

      onChange(
        isMultiSelect ? newValues : (newValues[0] ?? ''),
        isMultiSelect ? newData : newData[0],
      );
    },
    [onChange, isMultiSelect, preserveNonRemovableValues],
  );

  const MultiValue = useMemo(
    () =>
      memo(({ data }: { readonly data: Option<T> }) => (
        <Chip
          className="mr-1 first:-ml-1.5"
          label={data.label}
          onClick={
            onMultiValueClick
              ? () => {
                  onMultiValueClick(data);
                }
              : undefined
          }
          onDelete={
            !isRemovableValue || isRemovableValue?.(data)
              ? () => {
                  handleChange(
                    selectedItems.filter(({ value }) => value !== data.value),
                  );
                }
              : undefined
          }
        />
      )),
    [onMultiValueClick, handleChange, selectedItems, isRemovableValue],
  );

  const handleLoadOptions = useCallback<typeof loadOptions>(
    async (...arguments_: any[]) => {
      try {
        return await loadOptions(...arguments_);
      } catch (error) {
        setError(
          t(($) => $.selectServerDriven.error.loadOptions, {
            ns: 'ui',
          }),
        );

        console.error('Error loading options:', error);

        return {
          hasMore: false,
          options: [],
        };
      }
    },
    [loadOptions, t],
  );

  const additional = useMemo(
    () => ({
      filterActive,
      page: 1,
    }),
    [filterActive],
  );

  const memoizedStyles = useMemo(
    () => ({
      control: (provided: CSSProperties) => ({
        ...provided,
        minHeight: '2.5rem',
      }),
      indicatorsContainer: (provided: CSSProperties) => ({
        ...provided,
        alignItems: 'flex-start',
      }),
      menu: (provided: CSSProperties) => ({
        ...provided,
        margin: 0,
      }),
      menuPortal: (provided: CSSProperties) => ({
        ...provided,
        zIndex: 1500,
      }),
      placeholder: (provided: CSSProperties) => ({
        ...provided,
        color: 'hsl(213 1 60)', // TODO: use CSS variable text lighter once Tailwind is migrated to CSS variables
        fontStyle: 'italic',
      }),
      valueContainer: (provided: CSSProperties) => ({
        ...provided,
        paddingRight: 0,
        rowGap: '0.25rem',
      }),
      ...style,
    }),
    [style],
  );

  const totalErrors = useMemo(
    () => [error, errors].filter(Boolean).join(', '),
    [error, errors],
  );
  const hasErrors = Boolean(totalErrors);

  const memoizedClassNames = useMemo(
    () => ({
      control: () => (hasErrors ? 'border-tiga-semantic-error' : undefined),
      ...classNames,
    }),
    [hasErrors, classNames],
  );

  const memoizedComponents = useMemo(
    () => ({
      IndicatorSeparator: hideComponent,
      MenuList: wrapMenuList((props: any) => (
        <CustomMenuList
          {...props}
          isLoading={props.isLoading || false}
          options={props.options || []}
        />
      )),
      MultiValue,
    }),
    [MultiValue],
  );

  const selectedValue = useMemo(
    () => (isMultiSelect ? selectedItems : selectedItems[0] || null),
    [isMultiSelect, selectedItems],
  );

  return (
    <>
      <AsyncPaginate<Option<T>, GroupBase<Option<T>>, { page: number }>
        additional={additional}
        aria-label={ariaLabel}
        className={className}
        classNames={memoizedClassNames}
        components={memoizedComponents}
        debounceTimeout={debounceTime}
        formatOptionLabel={formatOptionLabel}
        inputId={id}
        isClearable={isClearable}
        isDisabled={isDisabled || !isItemSelectionAllowed()}
        isMulti={isMultiSelect}
        loadOptions={handleLoadOptions}
        loadingMessage={() => 'Lade Daten...'}
        menuPortalTarget={document.body}
        noOptionsMessage={() => 'Keine passenden Daten gefunden'}
        placeholder={placeholder}
        selectRef={selectRef}
        shouldLoadMore={shouldLoadMore}
        styles={memoizedStyles}
        value={selectedValue}
        onChange={handleChange}
      />
      {hasErrors ? <ValidationError>{totalErrors}</ValidationError> : null}
    </>
  );
};
